"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useItemListTracking = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _react = require("react");
var _useCookie = require("./useCookie");
var _eventTracking = require("../utils/eventTracking");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const VISIBILITY_THRESHOLD = 0.5;
const FLUSH_DELAY_MS = 400;
const getItemKey = item => {
  var _item$product, _item$product2;
  return `${((_item$product = item.product) === null || _item$product === void 0 ? void 0 : _item$product.productId) || ((_item$product2 = item.product) === null || _item$product2 === void 0 ? void 0 : _item$product2.id)}|${item.index}`;
};
const isIntersectionObserverSupported = () => typeof window !== 'undefined' && 'IntersectionObserver' in window && 'IntersectionObserverEntry' in window && 'intersectionRatio' in window.IntersectionObserverEntry.prototype;
const useItemListTracking = ({
  listId,
  listName,
  enabled = true
}) => {
  const {
    eventTracking
  } = (0, _useCookie.useCookie)();

  // Long-lived callbacks (observer, timers, effects) read the latest render
  // values through this ref instead of their creation-time closures.
  const latestRef = (0, _react.useRef)({
    listId,
    listName,
    enabled,
    eventTracking
  });
  latestRef.current = {
    listId,
    listName,
    enabled,
    eventTracking
  };
  const observerRef = (0, _react.useRef)(null);
  const trackedElementsRef = (0, _react.useRef)(new Map());
  const reportedKeysRef = (0, _react.useRef)(new Set());
  const pendingItemsRef = (0, _react.useRef)([]);
  const flushTimerRef = (0, _react.useRef)(null);
  const flushPendingItems = () => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const pending = pendingItemsRef.current;
    if (pending.length === 0) {
      return;
    }
    pendingItemsRef.current = [];
    pending.sort((a, b) => a.index - b.index);
    latestRef.current.eventTracking('view_item_list', {
      ecommerce: {
        item_list_id: pending[0].listId,
        item_list_name: pending[0].listName,
        items: pending.map(item => (0, _eventTracking.getEventItemFromProduct)(item.product, 1, {
          index: item.index,
          item_list_id: item.listId,
          item_list_name: item.listName
        }))
      }
    });
  };
  const reportItem = item => {
    const key = getItemKey(item);
    if (reportedKeysRef.current.has(key)) {
      return;
    }
    reportedKeysRef.current.add(key);
    const {
      listId,
      listName
    } = latestRef.current;
    pendingItemsRef.current.push(_objectSpread(_objectSpread({}, item), {}, {
      listId,
      listName
    }));
    if (flushTimerRef.current === null) {
      flushTimerRef.current = setTimeout(flushPendingItems, FLUSH_DELAY_MS);
    }
  };
  const getObserver = () => {
    if (!observerRef.current && isIntersectionObserverSupported()) {
      observerRef.current = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          var _observerRef$current;
          const item = trackedElementsRef.current.get(entry.target);
          // While disabled, entries stay observed so they can report later.
          if (!entry.isIntersecting || !item || !latestRef.current.enabled) {
            return;
          }
          (_observerRef$current = observerRef.current) === null || _observerRef$current === void 0 ? void 0 : _observerRef$current.unobserve(entry.target);
          trackedElementsRef.current.delete(entry.target);
          reportItem(item);
        });
      }, {
        threshold: VISIBILITY_THRESHOLD
      });
    }
    return observerRef.current;
  };
  const unregisterItem = key => {
    trackedElementsRef.current.forEach((tracked, element) => {
      if (getItemKey(tracked) === key) {
        var _observerRef$current2;
        (_observerRef$current2 = observerRef.current) === null || _observerRef$current2 === void 0 ? void 0 : _observerRef$current2.unobserve(element);
        trackedElementsRef.current.delete(element);
      }
    });
  };
  const registerItem = (element, item) => {
    var _getObserver;
    if (typeof window === 'undefined' || !(item !== null && item !== void 0 && item.product)) {
      return;
    }
    const key = getItemKey(item);
    if (!element) {
      unregisterItem(key);
      return;
    }
    if (reportedKeysRef.current.has(key)) {
      return;
    }
    if (!isIntersectionObserverSupported()) {
      reportItem(item);
      return;
    }
    trackedElementsRef.current.set(element, item);
    (_getObserver = getObserver()) === null || _getObserver === void 0 ? void 0 : _getObserver.observe(element);
  };
  const trackSelectItem = (product, index) => {
    if (!enabled || !product) {
      return;
    }
    eventTracking('select_item', {
      ecommerce: {
        item_list_id: listId,
        item_list_name: listName,
        items: [(0, _eventTracking.getEventItemFromProduct)(product, 1, {
          index,
          item_list_id: listId,
          item_list_name: listName
        })]
      }
    });
  };
  (0, _react.useEffect)(() => {
    return () => {
      var _observerRef$current3;
      flushPendingItems();
      (_observerRef$current3 = observerRef.current) === null || _observerRef$current3 === void 0 ? void 0 : _observerRef$current3.disconnect();
      observerRef.current = null;
      trackedElementsRef.current.clear();
      reportedKeysRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId, listName]);
  (0, _react.useEffect)(() => {
    const handlePageHide = () => flushPendingItems();
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return {
    registerItem,
    trackSelectItem
  };
};
exports.useItemListTracking = useItemListTracking;